import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode } from 'react';

import { NEUTRAL_TIER_COLOR, TIER_COLORS } from './constants';

import Avatar from '@/components/Avatar';
import { PoolTier, PoolWorker } from '@/services/preferredPoolsAPI';
import styled from '@/styles';

// ui-prototype/preferred-worker-pool/client-detail.html .cd-pool-member-row —
// the identity block member and nominee rows share (plan §5: nominees are
// evaluated against the same information). Tier badge renders a neutral "—"
// until dependability v2 supplies tiers (P16); Rookie pill per P9.

const Row = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 40,
  padding: '20px 12px 20px 0',
  borderBottom: '1px solid #EAEAEA',
  '&:last-child': { borderBottom: 'none' },
});

const Info = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  flex: 1,
  minWidth: 0,
});

const NameRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
});

const Name = styled('span', {
  fontSize: '14px',
  fontWeight: 700,
  color: '#1C1C1C',
  whiteSpace: 'nowrap',
});

const TierBadge = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  borderRadius: 8,
  padding: '3px 8px',
  fontSize: '12px',
  fontWeight: 700,
  color: '#fff',
});

const Pill = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 8,
  padding: '4px 8px',
  fontSize: '11px',
  fontWeight: 600,
  variants: {
    tone: {
      neutral: { background: '#E7F4E6', color: '#2E8722' },
      danger: { background: '#FDEAEA', color: '#CF040C' },
      // GRAVY-10395: readiness flag — same amber family as the staffing
      // modal's Unavailable chip.
      warning: { background: '#FEF3C7', color: '#92400E' },
    },
  },
  defaultVariants: { tone: 'neutral' },
});

const Stats = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: '14px',
  color: '#1C1C1C',
  whiteSpace: 'nowrap',
  flexWrap: 'wrap',
});

const StatsDot = styled('span', {
  width: 4,
  height: 4,
  borderRadius: '50%',
  background: '#DADADA',
});

const tierLabel = (tier: PoolTier | null): string =>
  tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : '—';

type Props = {
  worker: PoolWorker;
  tier: PoolTier | null;
  rookie: boolean;
  shiftsInSkillTotal: number;
  shiftsWithClient: number;
  skillName: string;
  inactive?: boolean;
  // GRAVY-10395: staffing-readiness reason ("Onboarding incomplete (…)").
  // Rendered only for ACTIVE members — inactive ones already carry the
  // Inactive pill, and a second flag would say the same thing twice.
  notReadyReason?: string | null;
  statsExtra?: ReactNode;
  actions?: ReactNode;
};

const WorkerRow = ({
  worker,
  tier,
  rookie,
  shiftsInSkillTotal,
  shiftsWithClient,
  skillName,
  inactive = false,
  notReadyReason = null,
  statsExtra,
  actions,
}: Props) => (
  <Row>
    <Info>
      <Avatar
        firstName={worker.name ?? ''}
        size="md"
        src={worker.avatar_url ?? undefined}
      />
      <div>
        <NameRow>
          <Name>{worker.name}</Name>
          <TierBadge
            data-testid="tier-badge"
            style={{
              background: tier ? TIER_COLORS[tier] : NEUTRAL_TIER_COLOR,
            }}
          >
            {tier && (
              <FontAwesomeIcon
                icon={faStar}
                style={{ width: 12, height: 12 }}
              />
            )}
            {tierLabel(tier)}
          </TierBadge>
          {rookie && <Pill>Rookie</Pill>}
          {inactive && <Pill tone="danger">Inactive</Pill>}
          {!inactive && notReadyReason && (
            <Pill tone="warning">{notReadyReason}</Pill>
          )}
        </NameRow>
        <Stats>
          <span>
            <b>{shiftsInSkillTotal}</b> {skillName.toLowerCase()} shifts
          </span>
          <StatsDot />
          <span>
            <b>{shiftsWithClient}</b> shifts with client
          </span>
          {statsExtra}
        </Stats>
      </div>
    </Info>
    {actions}
  </Row>
);

export default WorkerRow;
